import { LoadingStage } from "./shared";
import { IQuote } from "./quotes";

export type TripItemAvailability = "unknown" | "checking" | "available" | "unavailable";

/**
 * A person on the trip's traveller ROSTER. Defined once at the trip level
 * (via the header "Travellers" editor) and then referenced by id on each
 * product the person is booked onto. Mirrors the identifying field set the
 * checkout collects (`ISetCustomerData`).
 *
 * Note: product-specific "additional/optional" booking fields are NOT stored
 * here — the same person can answer them differently per product, so those
 * live on the item (`ITripItem.travellerOptionalData`, keyed by traveller id).
 */
export interface ITripTraveller {
    /** Stable client-generated id used to reference this person from items. */
    id: string;
    title: string;
    firstName: string;
    lastName: string;
    /** ISO YYYY-MM-DD. */
    dateOfBirth: string;
    email: string;
    phoneNumber: string;
    postalCode: string;
    countryCode: string;
}

/**
 * Per-product values for a product's additional/optional booking fields,
 * keyed by `optionalFieldId` (stringified). Matches the cart `optionalData`
 * shape. Stored per item, per traveller id (see
 * `ITripItem.travellerOptionalData`).
 */
export type TripOptionalData = Record<string, string | number>;

export interface ITripItem {
    /** Client-generated UUID for the item (used as key & dnd id). */
    id: string;
    productId: string;
    tdmsProductId: number;
    name: string;
    productImagePath?: string;
    productClass?: string | null;
    /** Zero-based offset from the trip's start date. */
    dayIndex: number;
    /** How many day-columns this item occupies (1 for activities, N for accommodation). */
    durationDays: number;
    /** Default fare info captured at the time the product was added. */
    fareTypeId: string | number;
    /** Human-readable fare name (e.g. "Twin Share"). Optional for legacy items. */
    fareName?: string;
    productPricesDetailsId: string | number;
    /**
     * Live-rate booking keys for externally-sourced accommodation rooms
     * (e.g. Hotelbeds). Captured from the accommodation availability search
     * and retained — NOT displayed — so the room can be booked at checkout.
     * Absent for CMS/generic suppliers and non-accommodation items.
     */
    defaultRateCode?: string;
    roomCode?: string;
    externalId?: string;
    externalSource?: string;
    /**
     * Availability-endpoint discriminator captured from the product detail at
     * add time. Together with `groupFaresForAvailabilityCheck` these drive
     * `shouldCheckAvailabilityByGroup`, so a re-check after a drag-drop move
     * hits the SAME endpoint the checkout calendar uses (SiteMinder
     * accommodation → checkAvailabilityByProductAndRange; otherwise the range
     * endpoint). Absent for items recovered from a saved quote (the backend
     * snapshot doesn't store it), which safely fall back to the range endpoint.
     */
    apiProviderId?: number;
    groupFaresForAvailabilityCheck?: boolean;
    /** Number of guests / units. Defaults to numPax of the fare. */
    quantity: number;
    /** numPax / step size from fare restrictions (1 if unknown). */
    numPax: number;
    rrp: number;
    bookingTimeId?: string | number | null;
    availabilityStatus: TripItemAvailability;
    availabilityMessage?: string;
    /**
     * Index of the matching row in the most recent `checkAvailRange` response
     * for this item's date. The FT cart/quote endpoints expect
     * `selectedAvailableIndices` to be the position(s) within the availability
     * response — sending a hardcoded `0` causes a 400 "Validation Error" when
     * the response is sorted or padded such that index 0 is a different date.
     * Captured during `checkTripItemAvailability`; defaults to 0 if unset.
     */
    availIndex?: number;
    /**
     * Monotonic counter bumped whenever any field that affects availability
     * changes (dayIndex, quantity, durationDays, trip startDate). Used to
     * (a) re-trigger the auto-check effect and (b) ignore stale fulfilled
     * responses from a request issued against an older version of the item.
     */
    availabilityVersion: number;
    /**
     * Ids of the roster people (see `ITrip.travellers`) booked onto this
     * product. The user ticks these in the per-product traveller picker; the
     * count should reconcile with `quantity` (numPax-aware). Absent/empty for
     * items the user hasn't assigned travellers to yet.
     */
    travellerIds?: string[];
    /**
     * Product-specific additional/optional booking field values, keyed first
     * by roster traveller id and then by `optionalFieldId`. Kept per item (not
     * on the roster person) because the same person can answer differently on
     * different products. Carried into the cart booking data on conversion.
     */
    travellerOptionalData?: Record<string, TripOptionalData>;
}

export interface ITrip {
    /** Quote id once persisted; undefined for an unsaved local trip. */
    id?: string | number;
    name: string;
    /** ISO date string (YYYY-MM-DD). */
    startDate: string;
    durationDays: number;
    /**
     * Default number of travellers for the trip. Used as the default
     * quantity when a product is added (snapped up to a multiple of the
     * fare's numPax). Existing items are not changed when this is updated.
     */
    numTravellers: number;
    /**
     * The trip's traveller ROSTER — the people travelling, defined once and
     * referenced by id from each item's `travellerIds`. Kept in sync with
     * `numTravellers` (its length is the people count). Empty for trips that
     * haven't had travellers entered yet (incl. freshly re-opened saved trips).
     */
    travellers: ITripTraveller[];
    items: ITripItem[];
}

export interface ITripSummary {
    id: string | number;
    name: string;
    startDate?: string;
    durationDays?: number;
    itemCount: number;
    totalPrice: number;
    raw: IQuote;
}

export interface ITripPlannerState {
    current: ITrip;
    savedTrips: ITripSummary[];
    loadStage: LoadingStage;
    saveStage: LoadingStage;
    convertStage: LoadingStage;
    lastError?: string;
    /**
     * Diagnostic snapshot captured every time `fetchMyTrips` runs. Surfaces
     * the raw count of quotes returned by `/quotes/my` and the first few
     * titles so we can see — directly on the empty-state UI — whether the
     * API returned nothing, returned quotes that just don't match the trip
     * prefix, or failed entirely. Keeps the diagnosis loop tight when My
     * Trips appears empty but the user knows trips exist.
     */
    myTripsDiagnostic?: {
        totalQuotes: number;
        matchedAsTrip: number;
        sampleTitles: string[];
        fetchedAt: string;
        error?: string;
    };
}

/** Title prefix used to mark quotes that originated from the trip planner. */
export const TRIP_TITLE_PREFIX = "[Trip] ";
